import { useEffect, useRef, useState } from 'react';
import type { SubmitTemporaryCreditApplicationResponse } from '@nadoutong/contracts';
import { TEMPORARY_CREDIT_CAP_CNY } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useSubmitTemporaryCreditApplication,
  useUploadTemporaryCreditEvidence,
} from '../api/credit.ts';
import { formatApprovalChainDescription } from './credit/approval-chain.ts';
import { useT } from '../i18n/index.ts';
import {
  attachmentMaxMbLabel,
  bytesToBase64,
  isOversizeAttachment,
} from '../lib/receipts.ts';
import { parseAmountInputToMinorUnits } from './formatters.ts';
import { useToast } from './Toast.tsx';
import { Button, Dialog, FormField, Input } from '../ui/index.ts';

/**
 * Temporary-credit application dialog (router#2000). Payment evidence
 * is required as text, an attachment, or both — both forms take the
 * same finance-approval path. Submit never activates the line.
 */

interface TemporaryCreditApplyDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly orgId: string;
  readonly customerEmail: string;
  readonly onSubmitted?: (response: SubmitTemporaryCreditApplicationResponse) => void;
}

interface PendingApply {
  readonly limitCnyCents: number;
  readonly expectedArrivalCnyCents: number;
  readonly expectedArrivalDate: string;
  readonly paymentEvidenceText?: string;
  readonly paymentEvidenceAttachmentKey?: string;
}

export function TemporaryCreditApplyDialog({
  open,
  onOpenChange,
  orgId,
  customerEmail,
  onSubmitted,
}: TemporaryCreditApplyDialogProps): JSX.Element {
  const t = useT();
  const toast = useToast();

  const [limit, setLimit] = useState('');
  const [expectedAmount, setExpectedAmount] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [evidenceText, setEvidenceText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [pendingApply, setPendingApply] = useState<PendingApply | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    if (open) {
      return;
    }
    setLimit('');
    setExpectedAmount('');
    setExpectedDate('');
    setEvidenceText('');
    setFile(null);
    setValidationError(null);
    setPendingApply(null);
  }, [open]);

  const upload = useUploadTemporaryCreditEvidence();
  const apply = useSubmitTemporaryCreditApplication();
  const busy = upload.isPending || apply.isPending;

  const parsedLimit = parseAmountInputToMinorUnits(limit);
  const limitCnyCents =
    parsedLimit.ok && parsedLimit.minorUnits > 0 ? parsedLimit.minorUnits : null;
  const parsedExpected = parseAmountInputToMinorUnits(expectedAmount);
  const expectedCnyCents =
    parsedExpected.ok && parsedExpected.minorUnits > 0 ? parsedExpected.minorUnits : null;
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(expectedDate.trim());
  const trimmedEvidence = evidenceText.trim();

  function finish(_response: SubmitTemporaryCreditApplicationResponse): void {
    toast.success(t('credit.temporary.apply.toast.pending'));
    onSubmitted?.(_response);
    onOpenChange(false);
  }

  function runApply(input: PendingApply): void {
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    apply
      .mutateAsync({
        orgId,
        requestedLimitCnyCents: input.limitCnyCents,
        expectedArrivalAmountCnyCents: input.expectedArrivalCnyCents,
        expectedArrivalDate: input.expectedArrivalDate,
        paymentEvidenceText: input.paymentEvidenceText,
        paymentEvidenceAttachmentKey: input.paymentEvidenceAttachmentKey,
      })
      .then((response) => finish(response))
      .catch(() => {
        setPendingApply(input);
      })
      .finally(() => {
        submitInFlightRef.current = false;
      });
  }

  function handleSubmit(): void {
    if (busy) {
      return;
    }
    if (pendingApply !== null) {
      setValidationError(null);
      runApply(pendingApply);
      return;
    }
    if (limitCnyCents === null) {
      setValidationError(t('credit.temporary.apply.error.limit'));
      return;
    }
    if (expectedCnyCents === null) {
      setValidationError(t('credit.temporary.apply.error.expected_amount'));
      return;
    }
    if (!dateOk) {
      setValidationError(t('credit.temporary.apply.error.expected_date'));
      return;
    }
    if (trimmedEvidence.length === 0 && file === null) {
      setValidationError(t('credit.temporary.apply.error.evidence'));
      return;
    }
    if (file !== null && isOversizeAttachment(file.size)) {
      setValidationError(t('credit.apply.error.oversize', { mb: attachmentMaxMbLabel() }));
      return;
    }
    setValidationError(null);

    const base: PendingApply = {
      limitCnyCents,
      expectedArrivalCnyCents: expectedCnyCents,
      expectedArrivalDate: expectedDate.trim(),
      ...(trimmedEvidence.length === 0 ? {} : { paymentEvidenceText: trimmedEvidence }),
    };

    if (file === null) {
      runApply(base);
      return;
    }
    const evidence = file;
    submitInFlightRef.current = true;
    void evidence
      .arrayBuffer()
      .then((buffer) =>
        upload.mutateAsync({
          orgId,
          fileName: evidence.name,
          contentType: evidence.type.length > 0 ? evidence.type : 'application/octet-stream',
          dataBase64: bytesToBase64(new Uint8Array(buffer)),
        }),
      )
      .then((uploaded) => {
        submitInFlightRef.current = false;
        runApply({
          ...base,
          paymentEvidenceAttachmentKey: uploaded.attachment.object_key,
        });
      })
      .catch(() => {
        submitInFlightRef.current = false;
      });
  }

  function handleFileChange(nextFile: File | null): void {
    if (nextFile !== null && isOversizeAttachment(nextFile.size)) {
      setValidationError(t('credit.apply.error.oversize', { mb: attachmentMaxMbLabel() }));
      setFile(null);
      if (fileInputRef.current !== null) {
        fileInputRef.current.value = '';
      }
      return;
    }
    setValidationError(null);
    setFile(nextFile);
  }

  const uploadError =
    upload.error !== null
      ? upload.error instanceof OpsApiError
        ? `${upload.error.code}: ${upload.error.message}`
        : upload.error.message
      : null;
  const applyError =
    apply.error !== null
      ? apply.error instanceof OpsApiError
        ? `${apply.error.code}: ${apply.error.message}`
        : apply.error.message
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) {
          onOpenChange(next);
        }
      }}
      title={t('credit.temporary.apply.title')}
      description={
        <span data-testid="temporary-credit-apply-description">
          {formatApprovalChainDescription('temporary_credit_application', t, {
            cap: String(TEMPORARY_CREDIT_CAP_CNY),
          })}
        </span>
      }
      closeLabel={t('common.close')}
      testId="temporary-credit-apply-dialog"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="temporary-credit-apply-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={busy}
            data-testid="temporary-credit-apply-confirm"
          >
            {pendingApply !== null ? t('credit.apply.retry') : t('credit.apply.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1 rounded-lg bg-well px-3 py-2.5">
          <span className="text-label text-paper-low">{t('credit.apply.customer')}</span>
          <span className="text-body font-medium text-paper">{customerEmail}</span>
          <span className="font-mono text-micro text-paper-low">{orgId}</span>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('credit.temporary.apply.limit_label')}
            fieldId="temporary-credit-apply-limit"
            required
            hint={t('credit.temporary.apply.limit_hint', { cap: String(TEMPORARY_CREDIT_CAP_CNY) })}
          >
            <Input
              id="temporary-credit-apply-limit"
              value={limit}
              onChange={(event) => setLimit(event.currentTarget.value)}
              placeholder="0.00"
              inputMode="decimal"
              disabled={pendingApply !== null}
              data-testid="temporary-credit-apply-limit"
            />
          </FormField>
          <FormField
            label={t('credit.temporary.apply.expected_amount_label')}
            fieldId="temporary-credit-apply-expected-amount"
            required
            hint={t('credit.temporary.apply.expected_amount_hint')}
          >
            <Input
              id="temporary-credit-apply-expected-amount"
              value={expectedAmount}
              onChange={(event) => setExpectedAmount(event.currentTarget.value)}
              placeholder="0.00"
              inputMode="decimal"
              disabled={pendingApply !== null}
              data-testid="temporary-credit-apply-expected-amount"
            />
          </FormField>
        </div>

        <FormField
          label={t('credit.temporary.apply.expected_date_label')}
          fieldId="temporary-credit-apply-expected-date"
          required
          hint={t('credit.temporary.apply.expected_date_hint')}
        >
          <Input
            id="temporary-credit-apply-expected-date"
            type="date"
            value={expectedDate}
            onChange={(event) => setExpectedDate(event.currentTarget.value)}
            disabled={pendingApply !== null}
            data-testid="temporary-credit-apply-expected-date"
          />
        </FormField>

        <FormField
          label={t('credit.temporary.apply.evidence_text_label')}
          fieldId="temporary-credit-apply-evidence-text"
          hint={t('credit.temporary.apply.evidence_text_hint')}
        >
          <Input
            id="temporary-credit-apply-evidence-text"
            value={evidenceText}
            onChange={(event) => setEvidenceText(event.currentTarget.value)}
            disabled={pendingApply !== null}
            data-testid="temporary-credit-apply-evidence-text"
          />
        </FormField>

        <FormField
          label={t('credit.temporary.apply.evidence_file_label')}
          fieldId="temporary-credit-apply-evidence-file"
          hint={t('credit.temporary.apply.evidence_file_hint', { mb: attachmentMaxMbLabel() })}
        >
          <input
            id="temporary-credit-apply-evidence-file"
            ref={fileInputRef}
            type="file"
            className="text-dense text-paper"
            disabled={pendingApply !== null}
            onChange={(event) => handleFileChange(event.currentTarget.files?.[0] ?? null)}
            data-testid="temporary-credit-apply-evidence-file"
          />
        </FormField>

        {validationError !== null ? (
          <p className="text-dense text-danger" data-testid="temporary-credit-apply-validation">
            {validationError}
          </p>
        ) : null}
        {uploadError !== null ? (
          <p className="text-dense text-danger" data-testid="temporary-credit-apply-upload-error">
            {uploadError}
          </p>
        ) : null}
        {applyError !== null ? (
          <p className="text-dense text-danger" data-testid="temporary-credit-apply-error">
            {applyError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
